import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Eye } from "lucide-react";
import api from "../api";
import { loadSchema, FIELD_TYPES, slugify } from "../formSchema";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { QuestionnairePreview } from "../components/QuestionnairePreview";
import { useToast } from "../hooks/use-toast";

const NEEDS_OPTIONS = ["select", "checkboxes"];

const Toggle = ({ on, onClick, label }) => (
  <button type="button" onClick={onClick}
    className="flex items-center gap-2 text-sm font-semibold"
    style={{ color: on ? "#8cff00" : "#8b8798" }}>
    <span className="inline-flex items-center rounded-full transition-colors" style={{ width: 38, height: 22, background: on ? "#8cff00" : "rgba(255,255,255,0.12)", padding: 2 }}>
      <span className="rounded-full bg-black transition-transform" style={{ width: 18, height: 18, transform: on ? "translateX(16px)" : "translateX(0)" }} />
    </span>
    {label}
  </button>
);

const QuestionnaireEditor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sections, setSections] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => { (async () => setSections(await loadSchema()))(); }, []);

  const update = (fn) => setSections((prev) => { const copy = JSON.parse(JSON.stringify(prev)); fn(copy); return copy; });

  const setField = (si, fi, key, value) => update((s) => { s[si].fields[fi][key] = value; });
  const setSection = (si, key, value) => update((s) => { s[si][key] = value; });

  const addField = (si) => update((s) => {
    s[si].fields.push({ name: `${slugify("new field")}_${Math.random().toString(36).slice(2, 6)}`, label: "New field", type: "text", required: false, full: true });
  });
  const removeField = (si, fi) => update((s) => { s[si].fields.splice(fi, 1); });
  const moveField = (si, fi, dir) => update((s) => {
    const arr = s[si].fields; const ni = fi + dir; if (ni < 0 || ni >= arr.length) return;
    [arr[fi], arr[ni]] = [arr[ni], arr[fi]];
  });

  const addSection = () => update((s) => { s.push({ title: "New Section", intro: "", fields: [] }); });
  const removeSection = (si) => update((s) => { s.splice(si, 1); });
  const moveSection = (si, dir) => update((s) => {
    const ni = si + dir; if (ni < 0 || ni >= s.length) return; [s[si], s[ni]] = [s[ni], s[si]];
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/form-schema", { sections });
      toast({ title: "Questionnaire saved", description: "The live form now reflects your changes." });
    } catch (e) {
      toast({ title: "Could not save", description: e?.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  if (!sections) return (
    <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin" style={{ color: "#8cff00" }} size={32} /></div>
  );

  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-white/10 bg-[#070410] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo height={38} />
            <span className="hidden sm:inline font-ui font-bold text-white uppercase tracking-wider text-sm">Questionnaire Editor</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm"><ArrowLeft size={16} /> Dashboard</button>
            <button onClick={() => setPreviewOpen(true)} data-testid="preview-questionnaire-btn" className="btn-green-outline"><Eye size={16} /> Preview</button>
            <button onClick={save} disabled={saving} className="btn-green">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <p className="text-white/55 text-sm mb-6">Edit sections and fields below. Toggle <span style={{ color: "#8cff00" }}>Required</span> per field. Changes go live on the public form as soon as you save. Existing submissions are never deleted.</p>

        {sections.map((sec, si) => (
          <div key={si} className="hss-card p-5 sm:p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-1">
                <input value={sec.title} onChange={(e) => setSection(si, "title", e.target.value)} className="hss-input font-ui font-extrabold uppercase" placeholder="Section title" />
                <input value={sec.intro || ""} onChange={(e) => setSection(si, "intro", e.target.value)} className="hss-input mt-2 text-sm" placeholder="Short section intro (optional)" />
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => moveSection(si, -1)} className="text-white/40 hover:text-white p-1"><ChevronUp size={18} /></button>
                <button onClick={() => moveSection(si, 1)} className="text-white/40 hover:text-white p-1"><ChevronDown size={18} /></button>
                <button onClick={() => removeSection(si)} className="text-red-300 hover:text-red-200 p-1"><Trash2 size={16} /></button>
              </div>
            </div>

            <div className="space-y-3">
              {sec.fields.map((f, fi) => (
                <div key={fi} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <GripVertical size={16} className="text-white/25" />
                    <input value={f.label} onChange={(e) => setField(si, fi, "label", e.target.value)} className="hss-input flex-1" placeholder="Field label / question" />
                    <button onClick={() => moveField(si, fi, -1)} className="text-white/40 hover:text-white p-1"><ChevronUp size={16} /></button>
                    <button onClick={() => moveField(si, fi, 1)} className="text-white/40 hover:text-white p-1"><ChevronDown size={16} /></button>
                    <button onClick={() => removeField(si, fi)} className="text-red-300 hover:text-red-200 p-1"><Trash2 size={15} /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-white/45 text-xs">Type</label>
                      <select value={f.type} onChange={(e) => setField(si, fi, "type", e.target.value)} className="hss-input mt-1">
                        {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/45 text-xs">Helper text (optional)</label>
                      <input value={f.helper || ""} onChange={(e) => setField(si, fi, "helper", e.target.value)} className="hss-input mt-1" />
                    </div>
                    {NEEDS_OPTIONS.includes(f.type) && (
                      <div className="sm:col-span-2">
                        <label className="text-white/45 text-xs">Options (one per line)</label>
                        <textarea rows={3} value={(f.options || []).join("\n")} onChange={(e) => setField(si, fi, "options", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="hss-input mt-1" />
                      </div>
                    )}
                    <div className="flex items-center gap-6 pt-2 sm:col-span-2">
                      <Toggle on={!!f.required} onClick={() => setField(si, fi, "required", !f.required)} label="Required" />
                      <Toggle on={!!f.full} onClick={() => setField(si, fi, "full", !f.full)} label="Full width" />
                      {["text", "textarea"].includes(f.type) && (
                        <div className="flex items-center gap-2">
                          <span className="text-white/45 text-xs">Max chars</span>
                          <input type="number" value={f.maxLength || ""} onChange={(e) => setField(si, fi, "maxLength", e.target.value ? parseInt(e.target.value, 10) : undefined)} className="hss-input" style={{ width: 90, padding: "0.4rem 0.6rem" }} />
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-white/25 text-[11px] mt-2">key: {f.name}</p>
                </div>
              ))}
            </div>

            <button onClick={() => addField(si)} className="btn-green-outline mt-4"><Plus size={15} /> Add field</button>
          </div>
        ))}

        <div className="flex items-center justify-between">
          <button onClick={addSection} className="btn-green-outline"><Plus size={16} /> Add section</button>
          <button onClick={save} disabled={saving} className="btn-green">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes</button>
        </div>
      </main>
      {previewOpen && <QuestionnairePreview sections={sections} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
};

export default QuestionnaireEditor;
